import { css } from '@emotion/react';
import { type FC, type HTMLAttributes, type ReactNode } from 'react';

import { ThemeTokens } from '../shared/styles';

const { media } = ThemeTokens;

export type StatGroupItem = {
  /** What the number counts, for example "acres". */
  label: ReactNode;
  /** The formatted value, for example "469,560". */
  value: ReactNode;
};

export type StatGroupTone = 'default' | 'inverse';

export type StatGroupSize = 'md' | 'lg';

export type StatGroupProps = HTMLAttributes<HTMLDListElement> & {
  items: StatGroupItem[];
  /** Use `inverse` on dark surfaces. */
  tone?: StatGroupTone;
  /**
   * `md` is one line of text with dot separators. `lg` shows each value as a
   * large number with its label underneath.
   */
  size?: StatGroupSize;
};

const COLORS: Record<StatGroupTone, { label: string; value: string }> = {
  default: {
    label: 'var(--color-neutral-800)',
    value: 'var(--color-primary-800)',
  },
  inverse: {
    label: 'var(--color-primary-100)',
    value: 'var(--color-base-white)',
  },
};

const listStyles = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-2) var(--spacing-3);
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
`;

const itemStyles = (tone: StatGroupTone) => css`
  display: inline-flex;
  align-items: center;
  gap: 0.3em;
  color: ${COLORS[tone].label};

  dd {
    order: 0;
    margin: 0;
    font-weight: 700;
    color: ${COLORS[tone].value};
  }

  dt {
    order: 1;
  }

  & + &::before {
    content: '';
    order: -1;
    width: 4px;
    height: 4px;
    margin-right: calc(var(--spacing-3) - 0.3em);
    border-radius: 50%;
    background: var(--color-primary-300);
  }
`;

const largeListStyles = css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--spacing-5) var(--spacing-6);
  margin: 0;

  ${media.md} {
    display: flex;
    flex-wrap: wrap;
    gap: var(--spacing-6) var(--spacing-16);
  }
`;

const largeItemStyles = (tone: StatGroupTone) => css`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);

  dd {
    order: 0;
    margin: 0;
    font-size: 24px;
    font-weight: 800;
    line-height: 1;
    letter-spacing: -0.02em;
    color: ${COLORS[tone].value};

    /* Matches the md heading size, one step below a page H1. */
    ${media.sm} {
      font-size: 32px;
    }

    ${media.md} {
      font-size: 40px;
    }
  }

  dt {
    order: 1;
    font-size: 15px;
    line-height: 1.4;
    color: ${COLORS[tone].label};
  }
`;

/**
 * A row of numbers with labels, such as "125 properties · 469,560 acres",
 * or large numbers with labels underneath (`size="lg"`).
 * Each value displays before its label; the markup keeps the term first.
 */
const StatGroup: FC<StatGroupProps> = ({
  items,
  tone = 'default',
  size = 'md',
  className,
  ...rest
}) => (
  <dl
    {...rest}
    className={className}
    css={size === 'lg' ? largeListStyles : listStyles}
  >
    {items.map((item, index) => (
      <div
        key={index}
        css={size === 'lg' ? largeItemStyles(tone) : itemStyles(tone)}
      >
        <dt>{item.label}</dt>
        <dd>{item.value}</dd>
      </div>
    ))}
  </dl>
);

StatGroup.displayName = 'StatGroup';

export default StatGroup;
